
import { useNavigate } from 'react-router-dom';
import { Button } from '@rabbit/elements/shared-components';
import { getGreeting } from '@rabbit/bizproc/react';
import { useMediaQuery } from '@rabbit/olive/hooks/useMediaQuery';
import ROUTE_NAME from '@rabbit/olive/utils/url-constants';
import logoiWarranty from '@rabbit/olive/assets/images/logo/logo-iwarranty-black.svg';
import styles from './HyClorTemplate.module.css';
import Footer from '@rabbit/olive/components/molecules/Footer/Footer';
import { useMyConsumerPersona } from '@rabbit/data/portal';

const items = [
  {
    id: 1,
    title: 'Warranties at your fingertips',
    description:
      'No trying to find old receipts, remembering phone calls or finding emails. Everything in one place. Easy and convenient.',
    img: 'https://mcusercontent.com/46d498bc614816f815d16c07b/images/230d56eb-f5b5-e0cf-a11d-5c7734d44404.png',
  },
  {
    id: 2,
    title: 'Easy claim process',
    description:
      'Need to make a claim. Easy! Log into the portal where you registered your product. Enter fault details and the process is underway.',
    img: 'https://mcusercontent.com/46d498bc614816f815d16c07b/images/d400a655-af68-cae4-87a1-696ae2f578a0.jpg',
  },
  {
    id: 3,
    title: 'Free to use',
    description:
      'Experience peace of mind in managing warranties with no service fees.',
    img: 'https://mcusercontent.com/46d498bc614816f815d16c07b/images/4646aa69-be2a-d659-5654-0e9e412d8327.jpg',
  },
];

function HyClorTemplate({
  onClickRegisterProduct,
}: {
  onClickRegisterProduct?: () => void;
}) {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const consumerPersona = useMyConsumerPersona();
  const consumerPersonaData = consumerPersona?.data.consumer_private;

  return (
    <>
      <main
        className={`${styles['font-gilroy-regular']} mx-auto max-w-[1326px] px-4 pb-14 text-gray-700 lg:pb-24`}
      >
        {/* header */}
        <header className="flex flex-wrap items-center gap-6 pt-10 pb-14">
          <div className="mr-auto flex gap-2 sm:gap-4">
            <img
              src="https://mcusercontent.com/46d498bc614816f815d16c07b/images/ab4ec5c3-0dca-42cf-f4ba-e3227302b8f3.png"
              alt="Hy-Clor"
              className="xs:h-8 h-6 lg:h-10"
            />
            <span className="border-r-2 border-gray-300" />
            <img
              src={logoiWarranty}
              alt="iWarranty"
              className="xs:h-8 h-6 lg:h-10"
            />
          </div>

          {!consumerPersonaData ? (
            <div className="inline-flex">
              <Button
                kind="primary"
                size="md"
                onClick={() => navigate(ROUTE_NAME.NON_LOGGED_CONTINUE_CLAIM)}
                className={`${styles['font-gilroy-bold']} lg:min-w-[100px min-w-[80px]`}
              >
                Login
              </Button>
            </div>
          ) : (
            <p className="text-lg font-bold text-gray-900">
              Hi{' '}
              {consumerPersonaData?.splitname?.first ||
                consumerPersonaData?.fullname}
              , good {getGreeting()}.
            </p>
          )}
        </header>

        {/* header-image */}
        <section className="mb-14 text-center">
          <img
            className="inline-block sm:w-2/3 lg:w-1/2"
            src="https://mcusercontent.com/46d498bc614816f815d16c07b/images/ff01c669-b846-697a-0f70-ef4b42319988.png"
            alt="Easy affordable poolcare"
          />
        </section>

        {/* hero-image */}
        <section
          className="relative flex aspect-[3/4] w-full flex-col justify-end overflow-hidden rounded-lg bg-cover bg-no-repeat p-8 sm:aspect-[16/6] sm:items-end sm:justify-center"
          style={{
            backgroundImage: `url(${
              isDesktop
                ? 'https://mcusercontent.com/46d498bc614816f815d16c07b/images/b620dc97-af56-586e-fb5a-8f447ffdca39.jpg'
                : 'https://mcusercontent.com/46d498bc614816f815d16c07b/images/758537a3-d657-6229-6fc0-ade0f5e3f6c6.jpg'
            })`,
          }}
        >
          <div className="space-y-6 text-white sm:w-1/2">
            <h1
              className={`${styles['font-gilroy-bold']} text-2xl leading-[1.2] lg:text-[36px] xl:text-[52px]`}
            >
              Register your Hy-Clor product today
            </h1>
            <p className="text-lg lg:text-2xl">
              Hy-Clor has partnered with iWarranty to give you convenience and
              peace of mind with your purchase.
            </p>
          </div>
        </section>

        {/* register-cta */}
        <section className="py-14 text-center">
          <Button
            kind="primary"
            onClick={onClickRegisterProduct}
            className={`${styles['font-gilroy-bold']} inline-flex min-h-[56px] min-w-full text-lg sm:min-w-[240px]`}
          >
            Register your product
          </Button>
        </section>

        {/* meta-text */}
        <p className="mb-14 text-lg lg:text-xl">
          At Hy-Clor, we want you, our customer, to have a great experience when
          buying and using our products. Our mission is to make pool and spa
          ownership, easy and affordable. We have partnered with iWarranty to
          ensure that should you have a problem with a Hy-Clor product, you have
          peace of mind that there is an easy, quick and fair process to resolve
          any problems. This means you can purchase any of our products with
          peace of mind knowing that we’ve got your back.
        </p>

        {/* experience section */}
        <section className="mb-14 flex flex-col-reverse gap-14 sm:flex-row sm:items-center lg:py-4">
          <div className="w-full">
            <h2
              className={`${styles['font-gilroy-bold']} mb-6 text-2xl leading-[1.2] text-gray-900 lg:text-[36px] xl:text-[52px]`}
            >
              How can Hy-Clor and iWarranty provide you the best post-purchase
              experience?
            </h2>

            <p className="text-lg lg:text-xl">
              Within iWarranty’s platform you can store, track and manage all
              your Hy-Clor products and warranties in one place. Accessible from
              anywhere, on any device, at any time.
            </p>
          </div>
          <div className="w-full sm:w-1/3">
            <img
              src="https://mcusercontent.com/46d498bc614816f815d16c07b/images/1d5a3e78-7bfd-5542-d193-ea36892e1afc.jpg"
              className="rounded-lg"
              alt="Lifestyle"
            />
          </div>
        </section>

        {/* thumbnails section */}
        <>
          {items.map((item) => {
            return (
              <section className="mb-14 flex flex-col-reverse gap-14 sm:flex-row sm:items-center sm:justify-between  sm:odd:flex-row-reverse lg:py-4">
                <div className="w-full sm:w-3/5 xl:w-1/2">
                  <h2
                    className={`${styles['font-gilroy-bold']} mb-6 text-2xl leading-[1.2] text-gray-900 lg:text-[36px] xl:text-[52px]`}
                  >
                    {item.title}
                  </h2>

                  <p className="text-lg lg:text-xl">{item.description}</p>
                </div>
                <div className="w-full sm:w-2/5">
                  <img src={item.img} className="rounded-lg" alt={item.title} />
                </div>
              </section>
            );
          })}
        </>

        {/* contact info */}
        <section className="flex flex-col">
          <h3
            className={`${styles['font-gilroy-bold']} mb-1 text-2xl text-gray-900`}
          >
            Hy-Clor service team
          </h3>
          <p className="mb-8 text-gray-700">
            Please reach out to us below if you have any specific questions.
          </p>

          <div className="flex flex-col gap-3 text-lg sm:flex-row sm:gap-8">
            <ul className="list-none space-y-3">
              <li>
                Email:{' '}
                <a className="text-gray-700" href="mailto:help@hyclor.com.au">
                  help@hyclor.com.au
                </a>
              </li>
              <li>
                Phone:{' '}
                <a className="text-gray-700" href="tel:+61 2 8805 2400">
                  +61 2 8805 2400
                </a>
              </li>
              <li>
                Helpline: AU{' '}
                <a className="text-gray-700" href="tel:1800 625 123">
                  1800 625 123
                </a>
              </li>
            </ul>
            <ul className="list-none space-y-3">
              <li>
                Helpline: NZ{' '}
                <a className="text-gray-700" href="tel:(09) 973 2477">
                  (09) 973 2477
                </a>
              </li>
              <li className="font-bold">Available Monday to Friday</li>
            </ul>
          </div>
        </section>
      </main>

      {/* footer */}
      <Footer className={`${styles['font-gilroy-regular']}`} />
    </>
  );
}

export default HyClorTemplate;
